/**
 * Persistence boundary for the home prayer draft. The draft can be the only
 * recoverable copy of a prayer, so both overwrite and removal are forbidden
 * while the lossless core-content cutover is read-only.
 */
import { isCoreReadOnly } from './core-cutover';

export const PRAYER_DRAFT_KEY = 'adia-prayer-draft';
export const PRAYER_DRAFT_AUDIO_IDS_KEY = 'adia-prayer-draft-audio-ids';
const QUEUE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_DRAFT_AUDIO_IDS = 16;

export function loadPrayerDraft(): string {
  try {
    return localStorage.getItem(PRAYER_DRAFT_KEY) ?? '';
  } catch {
    return '';
  }
}

export function persistPrayerDraft(value: string): boolean {
  if (isCoreReadOnly()) return false;
  try {
    if (value) localStorage.setItem(PRAYER_DRAFT_KEY, value);
    else localStorage.removeItem(PRAYER_DRAFT_KEY);
    return true;
  } catch {
    // Storage denial leaves the last durable value unchanged.
    return false;
  }
}

/**
 * Exact enhanced-voice queue identities whose durable transcripts were
 * appended to the still-uncommitted home draft. The companion key preserves
 * the historical plain-string format of `adia-prayer-draft` while letting a
 * reload consume only the audio rows that actually reached that draft.
 */
export function parsePrayerDraftAudioIds(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      (parsed as { version?: unknown }).version !== 1 ||
      !Array.isArray((parsed as { queueItemIds?: unknown }).queueItemIds)
    ) return [];
    const queueItemIds = (parsed as { queueItemIds: unknown[] }).queueItemIds;
    const accepted: string[] = [];
    for (const value of queueItemIds) {
      if (
        typeof value === 'string' &&
        QUEUE_ID_RE.test(value) &&
        !accepted.includes(value)
      ) accepted.push(value);
      if (accepted.length >= MAX_DRAFT_AUDIO_IDS) break;
    }
    return accepted;
  } catch {
    return [];
  }
}

export function loadPrayerDraftAudioIds(): string[] {
  try {
    return parsePrayerDraftAudioIds(localStorage.getItem(PRAYER_DRAFT_AUDIO_IDS_KEY));
  } catch {
    return [];
  }
}

export function persistPrayerDraftAudioIds(queueItemIds: Iterable<string>): void {
  if (isCoreReadOnly()) return;
  const accepted = parsePrayerDraftAudioIds(JSON.stringify({
    version: 1,
    queueItemIds: Array.from(queueItemIds),
  }));
  try {
    if (accepted.length === 0) {
      localStorage.removeItem(PRAYER_DRAFT_AUDIO_IDS_KEY);
    } else {
      localStorage.setItem(PRAYER_DRAFT_AUDIO_IDS_KEY, JSON.stringify({
        version: 1,
        queueItemIds: accepted,
      }));
    }
  } catch {
    // Storage denial leaves the last durable association unchanged.
  }
}
